import { ThemeProvider } from '@mui/material';
import React, { PropsWithChildren } from 'react';
import { theme } from '../lib/theme';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import i18n from '../misc/i18n';
import { de, enUS } from 'date-fns/locale';
import { PxtraApiClientProvider } from '../hooks/usePxtraApiClient';
import { RootStoreProvider } from '../hooks/useStore';
import { ActionsProvider } from '../hooks/useActions';
import { InformationDialogProvider } from '../hooks/useInfoDialog';
import { SnackbarProvider } from 'notistack';
import {
  DefaultFlag,
  ErrorFlag,
  InfoFlag,
  SuccessFlag,
  WarningFlag
} from '../lib/components/atomic/feedback/flag';
import { QueryClientProvider, QueryClient } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';

const i18nToLocale: { [key: string]: Locale } = {
  de: de,
  en: enUS
};

declare module 'notistack' {
  interface VariantOverrides {
    detailed: { details?: string };
    detailedInfo: { details?: string };
    detailedSuccess: { details?: string };
    detailedWarning: { details?: string };
    detailedError: { details?: string };
  }
}

const queryClient = new QueryClient();

export const Setup = ({ children }: PropsWithChildren) => {
  return (
    <QueryClientProvider client={queryClient}>
      <PxtraApiClientProvider>
        <RootStoreProvider>
          <LocalizationProvider
            dateAdapter={AdapterDateFns}
            adapterLocale={i18nToLocale[i18n.language]}
          >
            <ThemeProvider theme={theme}>
              <InformationDialogProvider>
                <SnackbarProvider
                  Components={{
                    default: DefaultFlag,
                    detailed: DefaultFlag,
                    error: ErrorFlag,
                    detailedError: ErrorFlag,
                    warning: WarningFlag,
                    detailedWarning: WarningFlag,
                    info: InfoFlag,
                    detailedInfo: InfoFlag,
                    success: SuccessFlag,
                    detailedSuccess: SuccessFlag
                  }}
                >
                  <ActionsProvider>{children}</ActionsProvider>
                </SnackbarProvider>
              </InformationDialogProvider>
            </ThemeProvider>
          </LocalizationProvider>
        </RootStoreProvider>
      </PxtraApiClientProvider>
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  );
};
